import { useCallback, useMemo, useState } from "react";
import {
  DragEndEvent,
  DragMoveEvent,
  DragOverEvent,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  AreaDoc,
  flattenAreas,
  getProjection,
  getSubtree,
  isAreaRow,
} from "../helper/area-tree";
import { ObjectType } from "./useObjectType";

/**
 * Drives dragging areas around the capture view. Areas render as one flat list
 * rather than nested sortable contexts, so a drag can change the parent as well
 * as the order: the slot the row lands in gives the position, the horizontal
 * offset gives the level. Levels that useAreaLogic rejects are never offered,
 * so an illegal structure cannot be dropped in the first place.
 */
export const useAreaDragAndDrop = ({
  areas,
  objectType,
  isExpanded,
  hasCracks,
  moveArea,
}: {
  areas: AreaDoc[];
  objectType: ObjectType | null;
  isExpanded: (id: string) => boolean;
  hasCracks: (areaId: string) => boolean;
  moveArea: (
    areaId: string,
    newParentId: string | null,
    newIndex: number
  ) => Promise<void>;
}) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [offsetLeft, setOffsetLeft] = useState(0);

  const sensors = useSensors(useSensor(PointerSensor));

  const rows = useMemo(
    () =>
      flattenAreas({
        areas,
        objectType,
        isExpanded,
        skipSubtreeOf: activeId ?? undefined,
      }),
    [areas, objectType, isExpanded, activeId]
  );

  const areaRows = useMemo(() => rows.filter(isAreaRow), [rows]);

  const sortableIds = useMemo(
    () => areaRows.map(({ id }) => ({ id })),
    [areaRows]
  );

  const subtree = useMemo(
    () => (activeId ? getSubtree(areas, activeId, hasCracks) : []),
    [areas, activeId, hasCracks]
  );

  const projection = useMemo(() => {
    if (!activeId || !overId) {
      return null;
    }

    return getProjection({
      items: areaRows,
      activeId,
      overId,
      dragOffset: offsetLeft,
      objectType,
      subtree,
    });
  }, [activeId, overId, offsetLeft, areaRows, objectType, subtree]);

  const activeRow = useMemo(
    () => areaRows.find((row) => row.id === activeId),
    [areaRows, activeId]
  );

  const reset = useCallback(() => {
    setActiveId(null);
    setOverId(null);
    setOffsetLeft(0);
  }, []);

  const handleDragStart = useCallback(({ active }: DragStartEvent) => {
    setActiveId(String(active.id));
    setOverId(String(active.id));
  }, []);

  const handleDragMove = useCallback(({ delta }: DragMoveEvent) => {
    setOffsetLeft(delta.x);
  }, []);

  const handleDragOver = useCallback(({ over }: DragOverEvent) => {
    setOverId(over ? String(over.id) : null);
  }, []);

  const handleDragEnd = useCallback(
    async ({ active }: DragEndEvent) => {
      const current = areaRows.find((row) => row.id === active.id);
      const target = projection;

      reset();

      if (!current || !target) {
        return;
      }

      const currentIndex = areas
        .filter((a) => (a.get("parentId") ?? null) === current.parentId)
        .findIndex((a) => a.id === current.id);

      if (
        current.parentId === target.parentId &&
        currentIndex === target.index
      ) {
        return;
      }

      await moveArea(String(active.id), target.parentId, target.index);
    },
    [areaRows, areas, projection, moveArea, reset]
  );

  return {
    sensors,
    rows,
    sortableIds,
    activeId,
    activeRow,
    projection,
    handleDragStart,
    handleDragMove,
    handleDragOver,
    handleDragEnd,
    handleDragCancel: reset,
  };
};
